import React, { ReactElement } from 'react';
import { Menu as AntMenu } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

interface ClickEvent<T> {
  key: T;
}

interface SideMenuItem<ItemKey> {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  visible?: boolean;
  key: ItemKey;
  children?: SideMenuItem<ItemKey>[];
  type?: 'group' | 'divider';
  href?: string;
  badge?: string | number;
}

interface SideMenuProps<ItemKey extends string> {
  items: SideMenuItem<ItemKey>[];
  current?: ItemKey;
  onClick: (event: ClickEvent<ItemKey>) => void;
  openKeys?: string[];
  onOpenChange?: (openKeys: string[]) => void;
  className?: string;
}

function SideMenu<ItemKey extends string>({
  items,
  current,
  onClick,
  openKeys,
  onOpenChange,
  className,
}: SideMenuProps<ItemKey>): ReactElement {
  const wrapLabel = (item: SideMenuItem<ItemKey>): SideMenuItem<ItemKey> => {
    const labelContent = item.badge ? (
      <MenuItemLabelWrapper>
        <span>{item.label}</span>
        <MenuBadge>{item.badge}</MenuBadge>
      </MenuItemLabelWrapper>
    ) : (
      item.label
    );

    if (!item.href) return { ...item, label: labelContent };
    return {
      ...item,
      label: (
        <a href={item.href} onClick={e => e.preventDefault()}>
          {labelContent}
        </a>
      ),
    };
  };

  const filterVisibleItems = (
    items: SideMenuItem<ItemKey>[],
  ): Omit<SideMenuItem<ItemKey>, 'visible'>[] => {
    return items.reduce((result, item) => {
      if ('visible' in item && item.visible === false) {
        return result;
      }
      const { visible, ...rest } = item;
      if (rest.children) {
        const filteredChildren = filterVisibleItems(rest.children);
        if (filteredChildren.length > 0) {
          result.push({ ...rest, children: filteredChildren as SideMenuItem<ItemKey>[] });
        }
      } else {
        result.push(wrapLabel(rest));
      }
      return result;
    }, [] as Omit<SideMenuItem<ItemKey>, 'visible'>[]);
  };

  const antOnClick = (e: { key: string }) => {
    onClick({ key: e.key as ItemKey });
  };

  return (
    <StyledMenu
      items={filterVisibleItems(items) as React.ComponentProps<typeof AntMenu>['items']}
      onClick={antOnClick}
      selectedKeys={current ? [current] : undefined}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      mode="inline"
      className={className}
    />
  );
}

const MenuItemLabelWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const MenuBadge = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background-color: ${colorsPalette.red500};
  color: ${colorsPalette.white};
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-left: 8px;
`;

const StyledMenu = styled(AntMenu)`
  --ant-menu-item-height: 40px;
  --ant-menu-item-border-radius: 8px;
  --ant-menu-item-margin-inline: 0px;
  --ant-menu-item-margin-block: 2px;
  --ant-menu-item-color: rgba(0, 0, 0, 0.88);
  --ant-menu-item-hover-color: ${colorsPalette.dustyBlue500};
  --ant-menu-item-selected-color: ${colorsPalette.dustyBlue500};
  --ant-menu-item-selected-bg: transparent;
  --ant-menu-item-active-bg: transparent;
  --ant-menu-sub-menu-item-bg: transparent;
  --ant-menu-icon-size: 16px;

  &.ant-menu {
    background: transparent;
    border-inline-end: none !important;
    padding: 4px;
    font-size: 16px;
    line-height: 22px;
  }

  &.ant-menu-inline .ant-menu-item {
    padding: 0 16px 0 12px !important;
    gap: 10px;
  }

  &.ant-menu-inline .ant-menu-submenu-title {
    padding: 0 16px 0 12px !important;
    gap: 10px;
  }

  &.ant-menu-inline .ant-menu-item-selected,
  &.ant-menu-light .ant-menu-item-selected,
  &.ant-menu-light > .ant-menu .ant-menu-item-selected {
    color: ${colorsPalette.dustyBlue500} !important;
    background-color: transparent !important;
  }

  &.ant-menu-inline .ant-menu-item-selected .anticon,
  &.ant-menu-light .ant-menu-item-selected .anticon {
    color: ${colorsPalette.dustyBlue500} !important;
  }

  &.ant-menu-inline .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: ${colorsPalette.dustyBlue500};
  }

  &.ant-menu-inline .ant-menu-submenu-selected > .ant-menu-submenu-title .anticon {
    color: ${colorsPalette.dustyBlue500};
  }

  &.ant-menu-inline .ant-menu-submenu-selected > .ant-menu-submenu-title .ant-menu-submenu-arrow {
    color: ${colorsPalette.dustyBlue500};
  }

  &.ant-menu .ant-menu-sub.ant-menu-inline {
    background: transparent;
    padding-bottom: 4px;
  }

  &.ant-menu .ant-menu-sub.ant-menu-inline .ant-menu-item {
    padding-left: 40px !important;
  }

  .ant-menu-item a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: inherit;
    }
  }

  &.ant-menu .ant-menu-sub.ant-menu-inline .ant-menu-item-selected {
    background-color: ${colorsPalette.white} !important;
  }
`;

export { SideMenu, type SideMenuItem, type SideMenuProps };
